<template>
  <RevForm
    class="flex flex-col gap-12"
    :initial-values
    :validate="validateForm"
  >
    <template #default="{ values, errors, isValid }">
      <div class="my-24 flex">
        <RevCarousel
          :alternative-button-label="i18n(translations.alternativeCurrentText)"
          :alternative-controller="i18n(translations.alternativeControllerText)"
          :alternative-current="i18n(translations.alternativeCurrentText)"
          :alternative-next="i18n(translations.alternativeCurrentText)"
          :alternative-previous="i18n(translations.alternativeCurrentText)"
          current-index-id="product-thumbnail-carousel"
        >
          <img
            v-for="image in productImages"
            :key="image.alt"
            :alt="image.alt"
            class="rounded-sm"
            :height="228"
            :src="image.src as string"
            :width="198"
          />
        </RevCarousel>
      </div>

      <h2 class="heading-1 mb-24">
        {{ productDetails?.title }}
      </h2>

      <div class="heading-3">
        {{ i18n(translations.listingDetails) }}
      </div>

      <div class="flex gap-8">
        <RevPicker
          v-for="aestheticGrade in aestheticGrades"
          :key="aestheticGrade.value"
          :label="aestheticGrade.label"
          :model-value="currentAestheticGrade === aestheticGrade.value"
          @update:model-value="onChooseAestheticGrade(aestheticGrade.value)"
        />
      </div>

      <div class="flex gap-8">
        <RevInputText
          id="sku"
          v-model="values.sku as string"
          class="flex-1"
          :error="errors.sku"
          :label="i18n(translations.sku)"
        />

        <RevInputText
          id="stock"
          v-model="values.stock as string"
          class="flex-1"
          :error="errors.stock"
          :label="i18n(translations.stock)"
          :min="1"
          type="number"
        />
      </div>

      <div class="mt-56 flex items-center gap-8">
        <span class="body-1-bold mr-12 flex-1 text-right">
          {{ i18n(translations.bulkPrice) }}
        </span>

        <RevInputSelect
          id="bulk-currency-select"
          v-model="bulkCurrency"
          class="flex-1"
          :label="i18n(translations.selectCurrency)"
          :options="bulkCurrencyOptions"
          placeholder="Select currency"
        />

        <RevInputText
          id="bulk-price-input"
          v-model="bulkPrice"
          class="flex-1"
          :label="i18n(translations.enterPrice)"
          type="number"
        />

        <RevButton
          :disabled="!bulkCurrency || !bulkPrice"
          variant="primary"
          @click="values.markets = applyBulkPrice(values)"
        >
          {{ i18n(translations.apply) }}
        </RevButton>
      </div>

      <div v-if="errors.markets" class="text-static-danger-hi body-1-bold mt-4">
        {{ errors.markets }}
      </div>

      <div
        v-for="(markets, currency) in groupedMarketsByCurrency"
        :key="currency"
      >
        <RevTable
          :collection="markets"
          :columns="getTableColumns(currency)"
          transparent-header
        >
          <template #body-market="{ item }">
            <div class="flex w-full justify-start">
              <Flag
                class="mr-12"
                :country-code="item.market as MarketCountryCode"
              />
              {{ i18n.country(item.market) }}
            </div>
          </template>

          <template #body-price="{ item }">
            <div class="flex w-full justify-end">
              <RevInputText
                :id="`price-${item.market}`"
                v-model="
                  (values.markets as Record<MarketCountryCode, string>)[
                    item.market as MarketCountryCode
                  ]
                "
                class="sm:min-w-[400px]"
                :label="i18n(translations.price, { currency: item.currency })"
                :min="0"
                type="number"
              />
            </div>
          </template>
        </RevTable>
      </div>

      <div class="mx-auto mt-32 flex w-2/5 flex-col gap-12">
        <RevButton
          :disabled="!isValid"
          :loading="isSubmitting"
          variant="primary"
          @click="
            saveListing({
              values,
              isValid,
              shouldRedirectToNewDraft: false,
            })
          "
        >
          {{ i18n(translations.saveDraft) }}
        </RevButton>

        <RevButton
          :disabled="!isValid"
          :loading="isSubmitting"
          variant="secondary"
          @click="
            saveListing({
              values,
              isValid,
              shouldRedirectToNewDraft: true,
            })
          "
        >
          {{ i18n(translations.saveDraftAndCreateNewOne) }}
        </RevButton>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed, ref } from 'vue'

import {
  HttpApiError,
  type MarketCountryCode,
  type Price,
} from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCarousel } from '@ds/components/Carousel'
import {
  FORM_VALID,
  RevForm,
  makeValidate,
  required,
} from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevPicker } from '@ds/components/Picker'
import { RevTable } from '@ds/components/Table'

import type { DraftListingBody } from '~/api/listings/product/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import translations from './ListingDetailsStep.translations'

const props = defineProps<{
  productDetails: Record<string, unknown> | null
  lastStepCurrentValues: Record<string, unknown> | null
}>()
const emit = defineEmits(['on-error'])

const { openErrorToast, openSuccessToast } = useTheToast()
const $httpFetch = useHttpFetch()
const i18n = useI18n()
const { activeMarkets } = useSellerInfoStore()
const marketplace = useMarketplace()

const isSubmitting = ref(false)
const bulkCurrency = ref('')
const bulkPrice = ref('')
const currentAestheticGrade = ref(0)
const aestheticGrades = [
  {
    label: 'Stallone',
    value: 4,
    code: 'STALLONE',
  },
  {
    label: 'Fair',
    value: 3,
    code: 'BRONZE',
  },
  {
    label: 'Good',
    value: 2,
    code: 'SILVER',
  },
  {
    label: 'Very good',
    value: 1,
    code: 'GOLD',
  },
  {
    label: 'Excellent',
    value: 0,
    code: 'SHINY',
  },
]
const isRequired = required(i18n(translations.required))
const validateForm = makeValidate<typeof initialValues>({
  aestheticGrade: [isRequired],
  sku: [isRequired],
  stock: [isRequired],
  markets: [
    (value) => {
      return Object.values(value as Record<string, string>).some(
        (price) => price !== '',
      )
        ? FORM_VALID
        : i18n(translations.priceError)
    },
  ],
})
const initialValues = {
  aestheticGrade:
    props.lastStepCurrentValues?.aestheticGrade || currentAestheticGrade.value,
  sku: props.lastStepCurrentValues?.sku || '',
  stock: props.lastStepCurrentValues?.stock || '',
  markets:
    props.lastStepCurrentValues?.markets ||
    ({} as Record<MarketCountryCode, string>),
}

const groupedMarketsByCurrency = computed(() => {
  const groups: Record<
    string,
    Array<{ market: string; price: null; currency: string }>
  > = {}
  for (const item of activeMarkets) {
    const currency = marketplace.allMarkets[item]?.defaultCurrency

    if (!currency) continue
    if (!groups[currency]) groups[currency] = []

    groups[currency].push({
      market: item,
      price: null,
      currency,
    })
  }

  return groups
})

const productImages = computed(() => {
  return [
    {
      src: props.productDetails?.mainImageUrl,
      alt: i18n(translations.mainImageUrl),
    },
    { src: props.productDetails?.imageUrl2, alt: i18n(translations.urlImage2) },
    { src: props.productDetails?.imageUrl3, alt: i18n(translations.urlImage3) },
    { src: props.productDetails?.imageUrl4, alt: i18n(translations.urlImage4) },
    { src: props.productDetails?.imageUrl5, alt: i18n(translations.urlImage5) },
    { src: props.productDetails?.imageUrl6, alt: i18n(translations.urlImage6) },
  ].filter((image) => image.src)
})

const bulkCurrencyOptions = computed(() =>
  Object.keys(groupedMarketsByCurrency.value).map((currency) => ({
    label: currency,
    value: currency,
  })),
)

function getTableColumns(currency: string) {
  return [
    {
      key: 'market',
      label: i18n(translations.marketWithCurrency, { currency }),
    },
    {
      key: 'price',
      label: i18n(translations.priceColumnLabel),
      align: 'end' as const,
      headerClassNames: 'w-full text-right',
    },
  ]
}

function onChooseAestheticGrade(aestheticGrade: number) {
  currentAestheticGrade.value = aestheticGrade
}

function applyBulkPrice(
  values: typeof initialValues,
): Record<MarketCountryCode, string> {
  if (!bulkCurrency.value || !bulkPrice.value)
    return { ...(values.markets as Record<MarketCountryCode, string>) }

  const markets = groupedMarketsByCurrency.value[bulkCurrency.value] || []
  const updatedMarkets = {
    ...(values.markets as Record<MarketCountryCode, string>),
  }

  for (const item of markets) {
    updatedMarkets[item.market as MarketCountryCode] = bulkPrice.value
  }

  return updatedMarkets
}

function getAestheticGradeCode(value: number): string {
  const found = aestheticGrades.find((grade) => grade.value === value)

  return found ? found.code : ''
}

async function saveListing({
  values,
  isValid,
  shouldRedirectToNewDraft,
}: {
  values: typeof initialValues
  isValid: boolean
  shouldRedirectToNewDraft: boolean
}) {
  isSubmitting.value = true
  if (isValid) {
    try {
      const prices: Record<string, Price> = {}

      for (const market of Object.keys(values.markets)) {
        const marketKey = market as MarketCountryCode
        const currency = marketplace.allMarkets[marketKey]?.defaultCurrency
        prices[marketKey] = {
          amount: (values.markets as Record<MarketCountryCode, string>)[
            marketKey
          ],
          currency,
        }
      }
      const body = {
        categoryId: props.productDetails?.categoryId as string,
        images: productImages.value.map((image) => image.src as string),
        fields: { ...props.productDetails },
        aestheticGradeCode: getAestheticGradeCode(
          values.aestheticGrade as number,
        ),
        // specialOfferTypeCode as static for the moment since we don't have the toggle for new battery
        specialOfferTypeCode: 'NORMAL',
        prices,
        quantity: Number(values.stock),
        sku: values.sku as string,
      }

      Object.entries(body.fields).forEach(([key, value]) => {
        body.fields[key] = value?.toString() || value
      })

      await $httpFetch('/api/seller-experience/drafts', {
        method: 'POST',
        body: body as DraftListingBody,
      })

      openSuccessToast({
        content: i18n(translations.listingSavedSuccessfully),
      })

      navigateTo({
        name: shouldRedirectToNewDraft
          ? ROUTE_NAMES.LISTINGS.PRODUCT.CREATE
          : ROUTE_NAMES.LISTINGS.INDEX,
      })
    } catch (error) {
      openErrorToast({
        content:
          HttpApiError.fromAnyError(error as Error).errors[0]?.title || '',
      })

      emit('on-error', {
        errors: HttpApiError.fromAnyError(error as Error).errors,
        currentValues: values,
      })
    } finally {
      isSubmitting.value = false
    }
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>
